import React, { useState, useEffect } from 'react';
import { Droplets, CloudRain, Battery } from 'lucide-react';
import { Device } from '../../types';
import deviceService from '../../services/deviceService';
import {
  SensorPanelsContainer,
  LeftColumn,
  RightColumn,
  SensorPanel,
  PanelHeader,
  PanelIcon,
  PanelTitle,
  SensorList,
  SensorRow,
  SensorLabel,
  SensorValue
} from '../../styles/SensorPanelStyles';

interface NetatmoRainDetailsProps {
  device: Device;
  deviceId: string;
}

interface NetatmoData {
  rain?: number;
  rain_1h?: number;
  rain_24h?: number;
  battery_percent?: number;
  rf_status?: number;
  last_seen?: string;
}

const NetatmoRainDetails: React.FC<NetatmoRainDetailsProps> = ({ device, deviceId }) => {
  const [latestData, setLatestData] = useState<NetatmoData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestData = async () => {
      try {
        const data = await deviceService.getLatestData(deviceId);
        if (data && data.decoded_json) {
          setLatestData(data.decoded_json);
        }
      } catch (error) {
        console.error('Error fetching latest data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestData();
  }, [deviceId]);

  const formatRain = (rain: number | undefined): string => {
    return rain !== undefined ? `${rain.toFixed(1)} mm` : 'N/A';
  };

  const formatBattery = (battery: number | undefined): string => {
    return battery !== undefined ? `${battery.toFixed(0)}%` : 'N/A';
  };

  const getRainIntensity = (rain: number | undefined): { text: string; status: 'normal' | 'warning' | 'critical' } => {
    if (rain === undefined || rain === 0) return { text: 'No Rain', status: 'normal' };
    if (rain < 2.5) return { text: 'Light Rain', status: 'normal' };
    if (rain < 10) return { text: 'Moderate Rain', status: 'warning' };
    if (rain < 50) return { text: 'Heavy Rain', status: 'critical' };
    return { text: 'Violent Rain', status: 'critical' };
  };

  const getBatteryStatus = (battery: number | undefined): 'normal' | 'warning' | 'critical' => {
    if (battery === undefined) return 'normal';
    if (battery > 60) return 'normal';
    if (battery > 20) return 'warning';
    return 'critical';
  };

  const getSignalQuality = (rfStatus: number | undefined): string => {
    if (rfStatus === undefined) return 'Unknown';
    if (rfStatus > 80) return 'Excellent';
    if (rfStatus > 60) return 'Good';
    if (rfStatus > 40) return 'Fair';
    return 'Weak';
  };

  const rainIntensity = getRainIntensity(latestData?.rain);

  if (loading) {
    return (
      <SensorPanelsContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          Loading rain gauge data...
        </div>
      </SensorPanelsContainer>
    );
  }

  return (
    <SensorPanelsContainer>
      <LeftColumn>
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <CloudRain size={16} />
            </PanelIcon>
            <PanelTitle>Precipitation Data</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Current Conditions</SensorLabel>
              <SensorValue status={rainIntensity.status}>{rainIntensity.text}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Current Rain Rate</SensorLabel>
              <SensorValue status="normal">{formatRain(latestData?.rain)}/h</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Rain Last Hour</SensorLabel>
              <SensorValue status="normal">{formatRain(latestData?.rain_1h)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Rain Last 24 Hours</SensorLabel>
              <SensorValue status="normal">{formatRain(latestData?.rain_24h)}</SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>

        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Droplets size={16} />
            </PanelIcon>
            <PanelTitle>Precipitation Intensity Guide</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>No Rain</SensorLabel>
              <SensorValue status="normal">0 mm/h</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Light Rain</SensorLabel>
              <SensorValue status="normal">&lt; 2.5 mm/h</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Moderate Rain</SensorLabel>
              <SensorValue status="warning">2.5-10 mm/h</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Heavy Rain</SensorLabel>
              <SensorValue status="critical">10-50 mm/h</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Violent Rain</SensorLabel>
              <SensorValue status="critical">&gt; 50 mm/h</SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>
      </LeftColumn>

      <RightColumn>
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Battery size={16} />
            </PanelIcon>
            <PanelTitle>Device Health</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Battery Level</SensorLabel>
              <SensorValue status={getBatteryStatus(latestData?.battery_percent)}>{formatBattery(latestData?.battery_percent)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Battery Status</SensorLabel>
              <SensorValue status={getBatteryStatus(latestData?.battery_percent)}>
                {latestData?.battery_percent === undefined ? 'Unknown' :
                 latestData.battery_percent > 60 ? 'Good' :
                 latestData.battery_percent > 20 ? 'Low' :
                 'Critical'}
              </SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Signal Quality</SensorLabel>
              <SensorValue status="normal">{getSignalQuality(latestData?.rf_status)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Last Seen</SensorLabel>
              <SensorValue status="normal">
                {latestData?.last_seen ? new Date(latestData.last_seen).toLocaleString() : 'N/A'}
              </SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>
      </RightColumn>
    </SensorPanelsContainer>
  );
};

export default NetatmoRainDetails;
